/**
 * Loading skeletons for the compliance workspace, in shadcn/ui's idiom: a
 * muted block with `animate-pulse`, shaped like the content it stands in for.
 *
 * Deliberately not the workspace's shimmer Skeleton: that gradient is
 * viewport-fixed and reads busy when a whole dashboard of bones animates at
 * once. The pulse is calmer at this scale, and each skeleton matches its
 * page's real layout so nothing jumps when the data lands.
 *
 * @module components/regulation/LoadingSkeletons
 */
import { cn } from "~/lib/utils";

/** shadcn's skeleton, verbatim: a pulsing muted block. */
export function Bone({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      aria-hidden
      data-slot="skeleton"
      className={cn("animate-pulse rounded-md bg-muted motion-reduce:animate-none", className)}
      {...props}
    />
  );
}

/** A bordered list card of `rows` placeholder rows — sections and tabs. */
export function ListSkeleton({
  rows = 5,
  className,
  label = "Loading",
}: {
  readonly rows?: number;
  readonly className?: string;
  readonly label?: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      aria-busy="true"
      className={cn(
        "divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card",
        className,
      )}
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 px-4 py-3">
          <Bone className="size-8 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Bone className="h-3.5 w-2/5" />
            <Bone className="h-3 w-3/5" />
          </div>
          <Bone className="h-5 w-16 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** The overview's exact bands: stat strip, two chart cards, recent list. */
export function OverviewSkeleton() {
  return (
    <div role="status" aria-label="Loading" className="grid gap-4 lg:grid-cols-2">
      <div className="grid divide-y divide-border/60 rounded-xl border border-border/60 bg-card sm:grid-cols-2 sm:divide-x lg:col-span-2 lg:grid-cols-4 lg:divide-y-0">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex flex-col gap-2 px-5 py-5">
            <Bone className="h-3.5 w-24" />
            <Bone className="h-8 w-14" />
            <Bone className="h-3 w-32" />
          </div>
        ))}
      </div>

      {Array.from({ length: 2 }, (_, index) => (
        <div key={index} className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex items-center gap-2.5">
            <Bone className="size-8 rounded-lg" />
            <Bone className="h-4 w-36" />
          </div>
          <Bone className="mt-4 h-44 w-full rounded-lg" />
        </div>
      ))}

      <div className="rounded-xl border border-border/60 bg-card lg:col-span-2">
        <div className="flex items-center gap-2.5 px-4 py-3">
          <Bone className="size-8 rounded-lg" />
          <Bone className="h-4 w-40" />
        </div>
        <div className="divide-y divide-border/60 border-t border-border/60">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3">
              <Bone className="size-8 shrink-0 rounded-lg" />
              <Bone className="h-4 min-w-0 flex-1 max-w-72" />
              <Bone className="ms-auto h-5 w-16 rounded-full" />
              <Bone className="h-3.5 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A settings-form stand-in: labelled field pairs. */
export function FormSkeleton({ fields = 4 }: { readonly fields?: number }) {
  return (
    <div role="status" aria-label="Loading" className="grid max-w-2xl gap-4 sm:grid-cols-2">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="space-y-1.5">
          <Bone className="h-3.5 w-24" />
          <Bone className="h-9 w-full" />
        </div>
      ))}
    </div>
  );
}

/** The proposal summary and two PDF passages used on Changes and in resolution. */
export function FindingChangesSkeleton() {
  return (
    <div role="status" aria-busy="true" className="space-y-6">
      <p className="text-sm text-muted-foreground">Preparing your changes…</p>
      <div className="space-y-4 rounded-xl border border-border/60 p-5 sm:p-6">
        <Bone className="h-4 w-2/5" />
        <Bone className="h-3 w-1/4" />
        <Bone className="h-3 w-full" />
        <Bone className="h-3 w-2/3" />
      </div>
      <div className="flex justify-between gap-4">
        <Bone className="h-4 w-24" />
        <Bone className="h-4 w-40" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Bone className="h-48" />
        <Bone className="h-48" />
      </div>
    </div>
  );
}
